import React from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, Quote, MapPin, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';

const TestimonialsPage = () => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`w-5 h-5 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  const handleCallClick = () => {
    window.location.href = 'tel:[TEL]';
  };

  // Extended reviews for demonstration
  const allReviews = [
    ...mockData.reviews,
    {
      id: 4,
      name: "Jean-Claude Moreau",
      location: "Tournefeuille",
      rating: 5,
      comment: "Réfection complète de ma toiture en tuiles mécaniques. Travail impeccable, équipe sérieuse et respectueuse. Le chantier a été livré dans les délais annoncés.",
      date: "2023-12-20",
      service: "Réfection toiture"
    },
    {
      id: 5,
      name: "Isabelle Rousseau",
      location: "Blagnac",
      rating: 5,
      comment: "Intervention rapide pour une fuite sur ma terrasse. Diagnostic précis, réparation efficace. Je recommande cette entreprise professionnelle.",
      date: "2023-12-15",
      service: "Étanchéité"
    },
    {
      id: 6,
      name: "Michel Bertrand",
      location: "Muret",
      rating: 5,
      comment: "Démoussage et traitement hydrofuge de ma toiture. Résultat parfait, comme neuve ! Prix très correct pour la qualité du service.",
      date: "2023-12-10",
      service: "Nettoyage & démoussage"
    },
    {
      id: 7,
      name: "Catherine Lemoine",
      location: "Balma",
      rating: 5,
      comment: "Réparation de gouttières et pose de nouvelles descentes. Travail soigné, matériaux de qualité. Très satisfaite du résultat.",
      date: "2023-12-05",
      service: "Zinguerie"
    },
    {
      id: 8,
      name: "Patrick Durand",
      location: "Colomiers",
      rating: 5,
      comment: "Changement complet de ma toiture ardoise. Équipe compétente, chantier propre, respect des délais. Excellente prestation !",
      date: "2023-11-28",
      service: "Couverture ardoise"
    },
    {
      id: 9,
      name: "Sylvie Martinez",
      location: "Toulouse",
      rating: 5,
      comment: "Dépannage en urgence suite à la tempête. Intervention très rapide, bâchage efficace puis réparation définitive. Merci pour votre réactivité !",
      date: "2023-11-20",
      service: "Dépannage urgence"
    }
  ];

  const averageRating = 4.8;
  const totalReviews = allReviews.length;

  return (
    <Layout>
      {/* Header Section */}
      <section className="hero section-sm">
        <div className="hero-content">
          <div className="container text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-6">
              Avis de nos clients
            </h1>
            <div className="flex items-center justify-center gap-2 text-2xl font-bold text-yellow-400 mb-4">
              <div className="flex items-center gap-1">
                {renderStars(5)}
              </div>
              <span>{averageRating}/5</span>
              <span className="text-gray-200 font-normal">({totalReviews} avis)</span>
            </div>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Découvrez les témoignages de nos clients satisfaits dans toute la région toulousaine. 
              Leur confiance est notre plus belle récompense.
            </p>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">{averageRating}/5</div>
              <div className="text-sm text-gray-600">Note moyenne</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">{totalReviews}</div>
              <div className="text-sm text-gray-600">Avis clients</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Clients satisfaits</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">+500</div>
              <div className="text-sm text-gray-600">Chantiers réalisés</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allReviews.map((review) => (
              <Card key={review.id} className="card-hover p-6 relative">
                <div className="space-y-4">
                  <Quote className="w-8 h-8 text-blue-600/30" />
                  
                  <div className="flex items-center gap-1">
                    {renderStars(review.rating)}
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed italic">
                    "{review.comment}"
                  </p>
                  
                  {review.service && (
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      {review.service}
                    </Badge>
                  )}
                  
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{review.name}</p>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" />
                          <span>{review.location}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(review.date).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 font-['Outfit'] mb-4">
              Pourquoi nous faire confiance ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-3">
                Qualité reconnue
              </h3>
              <p className="text-gray-600">
                Plus de 15 ans d'expérience et une note moyenne de 4,8/5 
                témoignent de notre expertise et de notre engagement qualité.
              </p>
            </Card>

            <Card className="p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-3">
                Proximité locale
              </h3>
              <p className="text-gray-600">
                Basés à Cugnaux, nous connaissons parfaitement les spécificités 
                climatiques et architecturales de la région toulousaine.
              </p>
            </Card>

            <Card className="p-8 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Quote className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-3">
                Transparence totale
              </h3>
              <p className="text-gray-600">
                Devis détaillés, suivi personnalisé et communication transparente 
                à chaque étape de votre projet.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-blue-600 text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
            Rejoignez nos clients satisfaits
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Faites confiance à notre expertise pour vos travaux de toiture. 
            Devis gratuit sous 24-48h !
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn bg-white text-blue-600 hover:bg-gray-100">
              Demander mon devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button 
              onClick={handleCallClick}
              className="btn btn-ghost text-white border-white hover:bg-white/10"
            >
              <Phone className="w-5 h-5 mr-2" />
              Nous appeler
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TestimonialsPage;